const { parseISO, isBefore, differenceInHours, format, differenceInYears, fromUnixTime, add } = require('date-fns');

const { toZonedTime, formatInTimeZone, getTimezoneOffset } = require('date-fns-tz');

const { sha256 } = require('js-sha256');

const timeZone = 'Europe/Paris';

/** JWT payload only (no verify). Replaces jwt.decode for browser + Node without pulling jsonwebtoken into the Vue bundle. */
function decodeJwtPayload (token) {
  if (!token || typeof token !== 'string') return null;
  try {
    const parts = token.split('.');
    if (parts.length < 2) return null;
    let base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    while (base64.length % 4) base64 += '=';
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
    return JSON.parse(new TextDecoder('utf-8').decode(bytes));
  } catch (e) {
    return null;
  }
}

const frenchDate = (t, date) => toZonedTime(date ? new Date(date) : new Date(), t || timeZone);

const frenchFormatedDate = (t, date, dateFormat) => formatInTimeZone(date ? new Date(date) : new Date(), t || timeZone, dateFormat);

const errorReferences = { ALREADY_EXISTS: 'ERROR_1', IS_HOLDER: 'ERROR_2' };

const replaceObjectFields = dataLayerObject => {
  Object.entries(dataLayerObject).map(([key, value]) => {
    if (Array.isArray(value)) {
      if (value.length > 1) {
        dataLayerObject[`${key}Array`] = value;
        delete dataLayerObject[key];
      } else {
        dataLayerObject[key] = value[0];
      }
    }
  });
};

const computeCommonAttribute = (product, offerInfo, siteInfo) => {
  const dataLayer = {
    productName: product.name,
    productId: offerInfo.horanet_product && offerInfo.horanet_product.horanet_product_reference ? offerInfo.horanet_product.horanet_product_reference : offerInfo.uuid,
    productFormula: product.practice_types,
    productFamily: product.areas.map(area => area.name),
    productActivity: product.activities,
    productTicketType: offerInfo.name.trim(),
    productStartingValidityDate: undefined,
    productPriceName: siteInfo && siteInfo.is_internal_session ? offerInfo.name.trim() : offerInfo.horanet_product.name.trim()
  };

  replaceObjectFields(dataLayer);

  return dataLayer;
};

const computeOptions = (options, isInternalSessions) => (options || []).map(option => {
  const period = option.price.period ? option.price.period : null;

  if (period) {
    delete period.__typename;

    if (period.renewedWith) {
      delete period.renewedWith.__typename;
    }
  }

  return {
    promo: option.promo,
    promoId: option.promoId,
    uuid: option.uuid,
    name: option.name,
    stock: option.stock,
    registrationFees: option.registrationFees,
    cancelation: option.cancelation,
    horanet_product_code: option.horanet_product && !isInternalSessions ? option.horanet_product.horanet_product_code.toString() : option.horanet_product_code || option.uuid,
    horanet_product_reference: option.horanet_product && !isInternalSessions ? option.horanet_product.horanet_product_reference : option.uuid,
    horanet_product_name: option.horanet_product ? option.horanet_product.name : option.name,
    unit_sales: option.detail.unit_sales,
    stage: option.detail.stage,
    stage1D: option.detail.stage1D,
    license: option.detail.license,
    pass: option.detail.pass,
    first_purchase: option.detail.first_purchase,
    reloadable: option.detail.reloadable,
    inscription: option.detail.inscription,
    reservation: option.detail.reservation,
    recurring: option.detail.recurring,
    price_category: option.price.category,
    tariffCode: option.price.tariffCode.toString(),
    tariffCategoryCode: option.price.tariffCategoryCode.toString(),
    price: parseInt(option.price.price),
    price_uuid: option.price.uuid,
    product_uuid: option.product_uuid,
    quantity: parseInt(option.quantity),
    session: option.detail.session,
    tva: option.price.tva,
    period: period,
    insuranceDetail: option.detail.insuranceDetail,
    withReservation: option.withReservation,
    reservationMetric: option.reservationMetric,
    reservationAmount: option.reservationAmount
  };
});

const computeTariff = tariff => {
  const promo = tariff.promo || (tariff.promotions && tariff.promotions.length ? tariff.promotions[0] : null);
  if (promo) {
    delete promo.__typename;
  }

  return {
    price_category: tariff.category,
    tariffCode: tariff.tariffCode.toString(),
    tariffCategoryCode: tariff.tariffCategoryCode.toString(),
    tariffMaxAge: tariff.tariffMaxAge,
    price: parseInt(tariff.price),
    price_uuid: tariff.uuid,
    tva: tariff.tva,
    gymlibCode: tariff.gymlibCode,
    promo
  };
};

const computeParticipant = (item, period, participant, isInternalSessions) => {
  // gymlibCode should only be in the tariff object, not at participant level
  // The GraphQL schema CartItemParticipantInput doesn't accept gymlibCode at participant level
  // It's already included in the tariff via computeTariff()
  const participantData = {
    uuid: participant.uuid,
    insurance: participant.insurance,
    options: computeOptions(item.options, isInternalSessions),
    tariff: computeTariff(item.hasOwnProperty('tariff') ? item.tariff : item),
    quantity: parseInt(item.quantity),
    period: period,
    insuranceDetail: item.insuranceDetail,
    horse: item.horse,
    horseName: item.horseName,
    nextYearLicense: participant.nextYearLicense,
    contractToRecharge: participant.contractToRecharge,
    contractRechargePurchase: participant.contractRechargePurchase
  };

  return participantData;
};

const computeDataLayerProps = (participant, price, quantity, options, session) => {
  const age = differenceInYears(new Date(), new Date(participant.birth_date));
  let participantAge = 'Enfant';
  if (age > 18) {
    participantAge = 'Adulte';
  } else if (age > 12 && age < 18) {
    participantAge = 'Ado';
  }

  let computedPrice = price * quantity;

  if (options && options.length) {
    let optionsPrice = 0;
    options.map(option => {
      optionsPrice += (option.price.price * option.quantity);
    });
    computedPrice += optionsPrice;
  }

  return {
    productParticipantAge: participantAge,
    productMarket: participantAge,
    productOptions: options && options.length > 0 ? 'avec option' : 'sans option',
    productOptionsArray: options && options.length > 0 ? options.map(option => option.name) : undefined,
    productPrice: parseFloat(computedPrice / 100),
    productPriceOP: undefined,
    productStartingValidityDate: session ? format(new Date(session.start_time), 'dd/MM/yyyy') : undefined,
    productQuantity: quantity
  };
};

const computeSession = (session, isInternalSession) => {
  let participants = session?.participants || [];
  if (typeof participants === 'string') {
    participants = JSON.parse(session.participants);
  }
  const participantsAmount = session.participantsAmount !== null && session.participantsAmount !== undefined ? session.participantsAmount : participants.length;

  let aditionalInfo = {};

  if (isInternalSession) {
    aditionalInfo = {
      participantsAmount: participantsAmount,
      maxParticipants: session.capacity !== null ? session.capacity : session.max_participant,
      recurring: session.recurring,
      recurring_uuid: session.recurring_uuid
    };
  }

  const { participants: sessionParticipants, ...sessionBody } = session;

  return {
    ...aditionalInfo,
    activity: {
      value: session.name.replace(/'/g, '%27'),
      name: session.name
    },
    code: session.id,
    start_date: session.start_time * 1000,
    end_date: session.end_time * 1000,
    last_time: session.recurring ? parseFloat(format(fromUnixTime(session.end_recurring_date), 'T')) : null,
    label: `${frenchFormatedDate(null, add(fromUnixTime(session.start_time), { seconds: 1 }), "HH'h'mm")} - ${frenchFormatedDate(null, add(fromUnixTime(session.end_time), { seconds: 1 }), "HH'h'mm")}`,
    // prefer pre-normalized placesDisponibles (Kala: participantsStock - contractsCount), fall back to legacy calculation
    placesDisponibles: session.placesDisponibles != null ? session.placesDisponibles : session.max_participant - participantsAmount,
    participantsAmount,
    detail: sessionBody,
    preReservedParticipants: session.pre_reserved_participants?.items || [],
    end_recurring_date: session.end_recurring_date
  };
};

const createSha256Hash = input => sha256(String(input));

const filterImpersonateModeResources = (item, isInternalSession, isImpersonateMode) => (isInternalSession && !isImpersonateMode && item.restrictedToImpersonate);

module.exports = {
  errorReferences,
  frenchDate,
  frenchFormatedDate,
  computeSession,
  createSha256Hash,
  filterImpersonateModeResources,
  getStripeKeys (workspace) {
    const stripeAccounts = workspace && process.env.VUE_APP_STRIPE_ACCOUNTS ? JSON.parse(process.env.VUE_APP_STRIPE_ACCOUNTS) : {};

    return {
      publicKey: process.env.VUE_APP_STRIPE_PUBLIC,
      accountId: stripeAccounts[workspace] || undefined
    };
  },
  // generates a domain to paybox redirection
  generateDomain (req) {
    // return process.env.VUE_APP_UCPA_DOMAIN;
    // return process.env.VUE_APP_FUNNEL_DOMAIN;
    const host = req.get('x-forwarded-host') || req.get('host');
    const protocol = host.includes('localhost') || host.includes('127.0.0.1') ? 'http' : 'https';

    return req.get('wt-host') || `${protocol}://${host}`;
  },
  setGTMDataLayer (siteInfo, cookie, user, path, funnelType, impersonateData) {
    // Google Tag Manager
    const environment = process.env.VUE_APP_ENVIRONMENT === 'production' ? 'prod' : 'preprod';

    const windowWidth = window.innerWidth;
    let device = 'desktop';
    if (windowWidth <= 480) {
      device = 'mobile';
    } else if (windowWidth <= 1025) {
      device = 'tablet';
    }

    // Get user Logged
    // TODO - verify token instead of just decoding it
    const decodedCookie = decodeJwtPayload(cookie);
    const authToken = decodedCookie?.cookieData || 'undefined';
    let userLogged = false;

    if (authToken) {
      if (authToken && authToken.uuid && isBefore(new Date(), parseISO(authToken.expires_in))) {
        userLogged = true;
      }
    }

    const diff = userLogged ? differenceInHours(new Date(), new Date(user.createTime)) : null;
    let userLoginProfile = null;
    if (userLogged) {
      userLoginProfile = diff > 24 ? 'connu' : 'nouveau';
    }
    let userSiteCustomerProfile = null;
    if (userLogged) {
      const knownSite = user.orders && user.orders.some(order => order.site_workspace === siteInfo.workspace);
      userSiteCustomerProfile = knownSite ? 'client connu' : 'nouveau client';
    }

    window.dataLayer.push({
      event: 'pageLoad',
      pagePath: `/loisirs-reservation${path}`,
      pageType: funnelType,
      environment: environment,
      layout: device,
      siteType: siteInfo.site_preroute,
      siteVersion: 'alpha',
      siteBrand: 'ucpa',
      siteName: siteInfo.site_name,
      userLogged: userLogged ? 'oui' : 'non',
      userID: userLogged ? authToken.uuid.replace('customer_', '') : null,
      userLoginProfile,
      userSiteCustomerProfile,
      mode: impersonateData ? 'impersonate' : 'front-client',
      is_impersonate_mode: !!impersonateData
    });
  },
  setGTMInfo ({ meta }, productArray) {
    let dataLayer = {
      event: 'ecommerceNEO',
      eventCommand: 'checkoutStep',
      checkoutStepNumber: meta.checkoutStepNumber,
      checkoutStepName: meta.checkoutStepName,
      checkoutStepActive: meta.checkoutStepActive ? meta.checkoutStepActive : 'non'
    };

    if (productArray && productArray.length && meta.productArray && meta.productArray.length) {
      dataLayer = {
        ...dataLayer,
        ...productArray
      };
    }
    window.dataLayer.push(dataLayer);
  },
  computeProductArrayByCart (cartItem, product, offerInfo, siteInfo) {
    const dataLayerAttribute = computeCommonAttribute(product, offerInfo, siteInfo);
    const response = { productArray: [] };

    if (!cartItem || cartItem.participants.length === 0) {
      const dataLayer = {
        ...dataLayerAttribute,
        productPrice: parseFloat(offerInfo.detail.lowest_price) || offerInfo.detail.lowest_price
      };

      response.productArray.push(dataLayer);

      return response;
    }

    const { participants, session } = cartItem;

    if (cartItem.hasOwnProperty('tariffs')) {
      cartItem.tariffs.map(tariff => {
        const participant = participants[0];

        const dataLayerProps = computeDataLayerProps(participant, tariff.price, tariff.quantity, tariff.options, tariff.session);

        const dataLayer = {
          ...dataLayerAttribute,
          ...dataLayerProps,
          productPriceRange: tariff.label.trim() || undefined
        };

        response.productArray.push(dataLayer);
      });
      return response;
    }

    participants.forEach(participant => {
      const { tariff, options, quantity } = participant;

      if (!tariff) {
        const dataLayer = {
          ...dataLayerAttribute,
          productPrice: parseFloat(offerInfo.detail.lowest_price) || offerInfo.detail.lowest_price
        };

        response.productArray.push(dataLayer);
        return;
      }

      const dataLayerProps = computeDataLayerProps(participant, tariff.price, quantity, options, session);

      // productParticipantAge
      const dataLayer = {
        ...dataLayerAttribute,
        ...dataLayerProps,
        productPriceRange: tariff.label.trim() || undefined
      };

      response.productArray.push(dataLayer);
    });
    return response;
  },
  computeCartItem (cartItem, isInternalSessions) {
    const cartItemProp = ((cartItem.details.unit_sales && !cartItem.details.pass) || (cartItem.details.inscription && ['STAGE'].includes(cartItem.details.offer_type) && ['alpha_ed'].includes(cartItem.workspace))) ? 'tariffs' : 'participants';

    const cartItemObject = {
      workspace: cartItem.workspace,
      participants: [],
      details: {
        ...cartItem.details,
        horanet_product_code: cartItem.details.horanet_product_code.toString(),
        activities: cartItem.details && cartItem.details.activities && cartItem.details.length ? cartItem.details.activities.map(a => a.uuid) : null
      }
    };

    if (cartItem.session) {
      cartItemObject.details.session = ['participants'].includes(cartItemProp) ? cartItem.session : {
        name: cartItem.session.name,
        code: cartItem.session.code,
        start_time: cartItem.session.startTime,
        end_time: cartItem.session.endTime,
        last_time: cartItem.session.lastTime
      };
    }

    cartItem[cartItemProp].forEach(item => {
      let period = null;
      if (cartItemProp === 'tariffs') {
        period = item.period ? item.period : null;
      } else {
        period = item.tariff.period ? item.tariff.period : null;
      }

      if (period) {
        delete period.__typename;
        if (period.renewedWith) {
          delete period.renewedWith.__typename;
        }
      }
      const participant = computeParticipant(item, period, item.uuid ? item : cartItem.participants[0], isInternalSessions);

      cartItemObject.participants.push(participant);
    });

    return cartItemObject;
  },
  computeDataLayerWithoutRoute (component) {
    let dataLayer = {
      event: 'ecommerceNEO',
      eventCommand: 'checkoutStep',
      checkoutStepActive: 'oui',
      tunnelType: 'tunnel abonnement'
    };
    switch (component) {
      case 'editUser':
        dataLayer = {
          ...dataLayer,
          checkoutStepName: 'coordonnées payeur',
          checkoutStepNumber: 3
        };
        break;
      case 'selectParticipant':
        dataLayer = {
          ...dataLayer,
          checkoutStepName: 'choix du participant',
          checkoutStepNumber: 4
        };
        break;
      case 'createParticipant':
        dataLayer = {
          ...dataLayer,
          checkoutStepName: 'coordonnées nouveau participant',
          checkoutStepNumber: 5
        };
        break;
      case 'editParticipant':
        dataLayer = {
          ...dataLayer,
          checkoutStepName: 'coordonnées participant',
          checkoutStepNumber: 5
        };
        break;
      case 'editEmergencyContact':
        dataLayer = {
          ...dataLayer,
          checkoutStepName: 'coordonnées contact d\'urgence',
          checkoutStepNumber: 6
        };
        break;
      default:
        break;
    }
    window.dataLayer.push(dataLayer);
  },
  computeCourtDataLayer (action, courtSettings, fullPath, product, offerInfo, user) {
    let dataLayer = {};
    if (action === 'reddirectToPersonalSpace') {
      dataLayer = {
        event: 'accessAccount',
        eventCommand: 'accessAccount',
        captain_transaction_id: undefined,
        access_source: 'confirmationPage'
      };

      window.dataLayer.push(dataLayer);
      return;
    }

    const { amountToPay } = courtSettings;
    const price = amountToPay && amountToPay !== 0 ? parseFloat(amountToPay / 100).toFixed(2) : 0;

    if (action === 'shareSession') {
      dataLayer = {
        event: 'shareSession',
        captain_transaction_id: undefined,
        participant_role: courtSettings.isInvite ? 'participant' : 'capitaine',
        share_method: undefined,
        participants_count: courtSettings?.session?.participantsAmount + 1 || 0,
        orderPaidAmount: price
      };

      window.dataLayer.push(dataLayer);
      return;
    }

    const checkoutSteps = {
      CourtProducts: { number: 1, name: 'choisissez un produit' },
      CourtSessions: { number: 2, name: 'Mon terrain' },
      CourtParticipants: { number: 3, name: 'Ma participation' },
      CourtOptions: { number: 4, name: 'Mes options' },
      CourtPayment: { number: 5, name: 'Mon moyen de paiement' },
      CourtConfirmation: {
        number: 6,
        name: `Confirmation de ta ${courtSettings.isInvite ? 'participation' : 'réservation'}`
      }
    };

    const step = checkoutSteps[action];
    if (!step) return;

    const hasStartDate = !!courtSettings?.session?.start_date;
    const hasEndDate = !!courtSettings?.session?.end_date;
    const startDate = new Date(courtSettings?.session?.start_date || null);
    const endDate = new Date(courtSettings?.session?.end_date || null);

    const productSlot = `${frenchFormatedDate(null, add(startDate, { seconds: 1 }), "HH'h'mm")}-${frenchFormatedDate(null, add(endDate, { seconds: 1 }), "HH'h'mm")}`;

    dataLayer = {
      event: 'ecommerceNEO',
      eventCommand: 'checkoutStep',
      pagePath: `/loisirs-reservation${fullPath}`,
      basketTotalValueNumber: courtSettings?.tariff?.price ? parseFloat((courtSettings?.tariff?.price / 100).toFixed(2)) : 0,
      basketParticipantsNumber: courtSettings?.session?.participantsAmount + 1 || 0,
      participant_role: courtSettings.isInvite ? 'participant' : 'capitaine',
      checkoutStepNumber: step.number,
      checkoutStepName: step.name,
      productArray: [{
        productName: product?.name || undefined,
        productId: offerInfo.horanet_product && offerInfo.horanet_product.horanet_product_reference ? offerInfo.horanet_product.horanet_product_reference : offerInfo.uuid,
        productFamily: (product.areas || []).map(area => area.name),
        productActivity: courtSettings?.session?.activity?.name || undefined,
        productTicketType: offerInfo?.name?.trim(),
        productStartingValidityDate: hasStartDate ? format(startDate, 'yyyy-MM-dd') : undefined,
        productPrice: courtSettings?.tariff?.price ? parseFloat((courtSettings?.tariff?.price / 100).toFixed(2)) : 0,
        productSlot: hasStartDate && hasEndDate ? productSlot : undefined,
        productDate: hasStartDate ? format(startDate, 'dd MMMM') : undefined,
        productSlotType: undefined
      }]
    };

    if (action === 'CourtConfirmation') {
      dataLayer = {
        ...dataLayer,
        orderPaymentMethodsArray: ['CB'],
        orderPaymentMode: undefined,
        orderID: undefined,
        orderCoupon: 'LeCodePromo',
        orderPaidAmount: price,
        captain_transaction_id: undefined,
        customerEmailAddressSHA256: createSha256Hash(user.email),
        customerPhoneNumberSHA256: createSha256Hash(user.mobilephone)
      };
    }

    window.dataLayer.push(dataLayer);
  },
  timezoneOffset ({ timezone = 'Europe/Paris', originalTimezone = 'Europe/Paris', date1, date2 }) {
    const offset1 = getTimezoneOffset(timezone, date1);
    const offset2 = getTimezoneOffset(originalTimezone, date2);

    return offset1 - offset2;
  }
};
